import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { useReveal } from "@/hooks/useReveal";
import { buildWhatsAppLink } from "@/lib/whatsapp";
import { getMacdConnectPagamentoStatus } from "@/lib/api";
import { Loader2 } from "lucide-react";

// Direct WhatsApp link for "salvar o contato" step — plain wa.me URL so it
// works reliably inside Instagram in-app browser.
const WHATSAPP_SAVE_LINK = buildWhatsAppLink(
  "Olá, acabei de me inscrever no MACD Connect Presencial em 31 de agosto.",
);

export default function MacdConnectConfirmado() {
  const rootRef = useReveal();
  const location = useLocation();
  const navigate = useNavigate();
  const nome = location.state?.nome || null;
  const firstName = nome ? nome.split(" ")[0] : null;
  const leadId = location.state?.lead_id || null;

  // Backend is the single source of truth for "paid". We never trust
  // location.state.paid alone — direct URL access with no lead_id, or with
  // a lead_id whose payment_status !== "paid", must NOT render the
  // confirmation copy.
  //   verifying → checking with backend (loading)
  //   paid       → payment_status === "paid" (render confirmation)
  //   redirect   → will be sent elsewhere; render nothing
  const [phase, setPhase] = useState("verifying");

  useEffect(() => {
    let cancelled = false;

    if (!leadId) {
      navigate("/macd-connect", { replace: true });
      return () => {};
    }

    (async () => {
      try {
        const s = await getMacdConnectPagamentoStatus(leadId);
        if (cancelled) return;
        if (s?.payment_status === "paid") {
          setPhase("paid");
        } else {
          // Not paid yet — send the user back to the payment page so they
          // can complete (or retry) the PIX. Preserve lead_id/nome.
          navigate("/macd-connect/pagamento", {
            replace: true,
            state: { lead_id: leadId, nome },
          });
        }
      } catch (_) {
        if (cancelled) return;
        // Lead not found or transient error — safest is back to the landing.
        navigate("/macd-connect", { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [leadId, nome, navigate]);

  useEffect(() => {
    document.title =
      phase === "paid"
        ? "Pagamento confirmado · MACD Connect Presencial · Oasys Academy"
        : "MACD Connect Presencial · Oasys Academy";
    if (phase === "paid") {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [phase]);

  if (phase !== "paid") {
    return (
      <div
        data-testid="mc-confirmado-verifying"
        className="min-h-screen bg-white flex items-center justify-center"
      >
        <Loader2 className="w-6 h-6 animate-spin text-[#0a0a0a]/60" />
      </div>
    );
  }

  return (
    <div ref={rootRef} data-testid="mc-confirmado-page" className="bg-white">
      <Navbar />
      <main className="pt-32 md:pt-40 pb-24 md:pb-32">
        <section className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="reveal">
            <div className="flex items-center gap-4 mb-8">
              <span className="rule-yellow" />
              <span className="eyebrow" data-testid="mc-confirmado-eyebrow">
                Pagamento confirmado
              </span>
            </div>
            <h1
              data-testid="mc-confirmado-title"
              className="font-display text-4xl md:text-6xl lg:text-[4.2rem] leading-[1.02] tracking-tight text-[#0a0a0a]"
            >
              Sua vaga está confirmada,{" "}
              <br />
              <em className="italic">
                {firstName ? `${firstName}.` : "por aqui."}
              </em>
            </h1>
            <div className="mt-10 max-w-2xl space-y-5 text-lg md:text-xl leading-[1.5] text-[#2a2a2a]">
              <p>
                Seu pagamento do{" "}
                <span className="font-medium text-[#0a0a0a]">
                  MACD Connect Presencial
                </span>{" "}
                foi confirmado. Sua vaga está garantida.
              </p>
              <p>
                <em className="italic">31 de agosto de 2026</em>, das{" "}
                <span className="font-medium">10h às 18h30</span>, em São Paulo.
                A confirmação da vaga e o endereço exato serão enviados pelo WhatsApp.
              </p>
            </div>
          </div>

          <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e5e2db] border border-[#e5e2db] reveal">
            {/* Step 01 */}
            <div
              data-testid="mc-confirmado-step-01"
              className="bg-white p-8 md:p-10 flex flex-col"
            >
              <span className="font-display text-2xl text-[#0a0a0a]/30">01</span>
              <h3 className="font-display text-2xl md:text-3xl mt-6 text-[#0a0a0a] tracking-tight">
                Reserve a data.
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-[#3a3a3a]">
                31 de agosto de 2026, das 10h às 18h30. São Paulo. Reserve o
                dia inteiro na agenda.
              </p>
            </div>

            {/* Step 02 */}
            <div
              data-testid="mc-confirmado-step-02"
              className="bg-white p-8 md:p-10 flex flex-col"
            >
              <span className="font-display text-2xl text-[#0a0a0a]/30">02</span>
              <h3 className="font-display text-2xl md:text-3xl mt-6 text-[#0a0a0a] tracking-tight">
                Confirme pelo WhatsApp.
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-[#3a3a3a]">
                Nossa equipe confirma sua vaga, envia o endereço e as
                orientações finais pelo WhatsApp da Academy.
              </p>
              <a
                href={WHATSAPP_SAVE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mc-confirmado-whatsapp-save"
                className="btn-primary mt-8 self-start uppercase tracking-[0.08em] text-[0.72rem]"
              >
                Confirmar minha participação no WhatsApp
                <span aria-hidden>→</span>
              </a>
            </div>

            {/* Step 03 */}
            <div
              data-testid="mc-confirmado-step-03"
              className="bg-white p-8 md:p-10 flex flex-col"
            >
              <span className="font-display text-2xl text-[#0a0a0a]/30">03</span>
              <h3 className="font-display text-2xl md:text-3xl mt-6 text-[#0a0a0a] tracking-tight">
                Comece essa leitura.
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-[#3a3a3a]">
                Enquanto o dia 31 não chega, conheça os insights da Oasys
                Academy sobre carreira, cliente, tecnologia e mercado.
              </p>
              <Link
                to="/#insights"
                data-testid="mc-confirmado-cta-insights"
                className="btn-ghost mt-8 self-start"
              >
                Conhecer os insights
                <span aria-hidden>→</span>
              </Link>
            </div>
          </div>

          <div className="mt-16 md:mt-20 flex flex-wrap items-center gap-6 reveal">
            <Link
              to="/"
              data-testid="mc-confirmado-cta-home"
              className="text-link-arrow"
            >
              Voltar para o site
              <span aria-hidden className="arrow">→</span>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
